import { useEffect, useState } from 'react';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';

export const useIsMultiSelectModifierPressed = () => {
  const [isPressed, setIsPressed] = useState(false);

  useEffect(() => {
    const isMacOs = getIsMacOs();
    const commandKeys = isMacOs
      ? ['MetaLeft', 'MetaRight']
      : ['ControlLeft', 'ControlRight'];
    const shiftKeys = ['ShiftLeft', 'ShiftRight'];
    const targetKeys = [...commandKeys, ...shiftKeys];

    const onKeyDown = (e: KeyboardEvent) => {
      if (targetKeys.includes(e.code) || targetKeys.includes(e.key)) {
        setIsPressed(true);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      // We check if ANY modifier is still pressed, not just if the released key was one of them.
      // However, since we can't easily poll, and this is for a general "is multi-selecting" state,
      // checking the specific key up is usually enough, but let's be robust.
      // Actually checking e.metaKey or e.shiftKey on keyUp is better.

      /*
        If the user releases one modifier but holds another, we still want to be true.
        e.shiftKey etc. reflect the state *after* the event (usually) or during.
        On keyup, the released key's modifier flag might be false.
      */

      if (targetKeys.includes(e.code) || targetKeys.includes(e.key)) {
        // simple check: if any of the target modifiers are held
        // We can use the event's modifier flags
        const isCmd = isMacOs ? e.metaKey : e.ctrlKey;
        const isShift = e.shiftKey;

        // Note: e.shiftKey might be false if Shift was just released.
        // So if the event key IS Shift, e.shiftKey is false (on keyup).

        // Wait, we want to know if *any* are currently pressed.
        // If I release Shift, and I'm not holding Cmd, then false.
        // If I release Shift, but I AM holding Cmd, then true.

        // e.metaKey / e.ctrlKey / e.shiftKey status on keyup event:
        // If I release Shift, e.shiftKey is false.
        // If I hold Cmd while releasing Shift, e.metaKey should be true.

        const stillPressed = isCmd || isShift;
        setIsPressed(stillPressed);
      }
    };

    // Safety clear on blur
    const onBlur = () => setIsPressed(false);

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return isPressed;
};
